import type { ActivityOf } from '@wyn/content';
import { assertGame } from '../errors.ts';
import { createRng } from '../rng.ts';
import { type MinigameResult, plural, type TurnEngineImpl } from './types.ts';

export const MINE_W = 6;
export const MINE_H = 6;
const COLLAPSES = 2;

type Rock = 'rock' | 'ore' | 'unstable';

/** Private: the whole face, including what has not been dug. Never sent to the client until the end. */
export interface MiningState {
  cells: Rock[];
  open: boolean[];
  swings: number;
  found: number;
  collapses: number;
  over: boolean;
  caveIn: boolean;
}

export type MiningMove = { type: 'swing'; cell: number } | { type: 'leave' };

/** What a dug cell shows: ore, a collapse, or bare rock with a count of touching ore and a crack warning. */
export type MiningCellView = null | { kind: 'ore' } | { kind: 'unstable' } | { kind: 'rock'; ore: number; cracked: boolean };

export interface MiningView {
  width: number;
  height: number;
  cells: MiningCellView[];
  swings: number;
  found: number;
  ore: number;
  collapses: number;
  maxCollapses: number;
  over: boolean;
  /** The full face, once the attempt is over. */
  revealed: Rock[] | null;
}

const neighbours = (i: number) => {
  const x = i % MINE_W, y = Math.floor(i / MINE_W), out: number[] = [];
  for (let dy = -1; dy <= 1; dy++)
    for (let dx = -1; dx <= 1; dx++) {
      const X = x + dx, Y = y + dy;
      if ((dx || dy) && X >= 0 && X < MINE_W && Y >= 0 && Y < MINE_H) out.push(Y * MINE_W + X);
    }
  return out;
};

/**
 * The Deep Shaft: a minesweeper-like rock face. Glints count touching ore, cracks warn of an unstable
 * pocket next door, and a second collapse caves the tunnel in. Ported from the Harrowmere prototype.
 */
export const mining: TurnEngineImpl<ActivityOf<'mining'>, MiningState, MiningMove, MiningView> = {
  init(a, level, serverSeed) {
    const n = MINE_W * MINE_H;
    const order = createRng(serverSeed).shuffle([...Array(n).keys()]);
    const cells: Rock[] = Array.from({ length: n }, () => 'rock');
    for (const i of order.slice(0, a.ore)) cells[i] = 'ore';
    for (const i of order.slice(a.ore, a.ore + a.unstable)) cells[i] = 'unstable';
    return { cells, open: cells.map(() => false), swings: 12 + Math.floor(level / 4), found: 0, collapses: 0, over: false, caveIn: false };
  },

  move(a, s, m) {
    assertGame(!s.over, 'INVALID', 'You have already left the mine.');
    if (m.type === 'leave') return { ...s, over: true };
    assertGame(Number.isInteger(m.cell) && m.cell >= 0 && m.cell < s.cells.length, 'INVALID', 'There is no rock there.');
    assertGame(!s.open[m.cell], 'INVALID', 'You have already dug there.');
    const open = s.open.map((o, i) => o || i === m.cell);
    let { found, collapses } = s;
    const kind = s.cells[m.cell]!;
    if (kind === 'ore') found++;
    if (kind === 'unstable') collapses++;
    const swings = s.swings - 1;
    const caveIn = collapses >= COLLAPSES;
    return { ...s, open, found, collapses, swings, caveIn, over: caveIn || found >= a.ore || swings <= 0 };
  },

  view(a, s) {
    return {
      width: MINE_W,
      height: MINE_H,
      cells: s.cells.map((kind, i): MiningCellView => {
        if (!s.open[i]) return null;
        if (kind !== 'rock') return { kind };
        const n = neighbours(i);
        return { kind: 'rock', ore: n.filter((j) => s.cells[j] === 'ore').length, cracked: n.some((j) => s.cells[j] === 'unstable') };
      }),
      swings: s.swings,
      found: s.found,
      ore: a.ore,
      collapses: s.collapses,
      maxCollapses: COLLAPSES,
      over: s.over,
      revealed: s.over ? s.cells : null,
    };
  },

  result(a, s): MinigameResult | null {
    if (!s.over) return null;
    const kept = s.caveIn ? Math.floor(s.found / 2) : s.found;
    const ingots = kept ? Math.max(1, Math.round(kept * 0.7)) : 0;
    return {
      score: s.found / a.ore,
      title: s.caveIn ? 'Cave-in!' : 'You climb out of the mine',
      text: s.caveIn ? `The tunnel caves in; you drag out ${kept} of ${s.found} ore.` : `You haul ${plural(s.found, 'lump')} of ore to the smelter.`,
      breakdown: [['Ore found', s.found / a.ore]],
      yield: ingots ? { itemKey: a.ingot, quantity: ingots, graded: false } : null,
      coin: 0,
      xpMult: 1,
      feats: [
        ...(s.found >= a.ore ? ['all-ore'] : []),
        ...(s.found >= a.ore && s.collapses === 0 ? ['no-collapse'] : []),
        ...(s.caveIn ? ['cave-in'] : []),
        ...(s.found === 0 && !s.caveIn ? ['empty-handed'] : []),
      ],
    };
  },
};
